import React, { useState, useEffect } from 'react';
import useScrollLock from '../utils/useScrollLock';
import {
  CANCEL_REASONS as REASON_KEYS, CANCEL_NOTE_MAX, cancelReasonsFor, cancelPolicy, formatGrace,
  cancelFollowUp, cancelReasonValue,
} from '../utils/cancelFlow';

// Kept for older imports; the list itself lives in utils/cancelFlow.js.
export const CANCEL_REASONS = REASON_KEYS;

const reasonLabel = (t, key, stage) => (
  stage === 'searching' && key === 'wait_too_long'
    ? t('cancel.reasonsSearching.wait_too_long')
    : t(`cancel.reasons.${key}`)
);

/** The fee / free-window line, shown on the follow-up step. */
export function CancelPolicyBanner({ t, policy }) {
  if (!policy || policy.kind === 'none') return null;
  const variant = { grace: 'free', courtesy: 'warning', fee: 'fee' }[policy.kind];
  const title = policy.kind === 'grace'
    ? t('cancel.graceTitle', { time: formatGrace(policy.graceSecLeft) })
    : t(policy.kind === 'fee' ? 'cancel.policyFeeTitle' : 'cancel.policyCourtesyTitle');
  const body = policy.kind === 'grace'
    ? t('cancel.graceBody')
    : t(policy.kind === 'fee' ? 'cancel.policyFeeBody' : 'cancel.policyCourtesyBody');
  return (
    <div className={`cancel-policy-banner ${variant}`} role="note">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="policy-icon">
        <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6z" />
      </svg>
      <div>
        <strong>{title}</strong>
        <p>{body}</p>
      </div>
    </div>
  );
}

/** Step 1: the reasons. Picking one moves on; nothing is cancelled here. */
export function CancelReasonsStep({ t, stage, onPick, onClose }) {
  const searching = stage === 'searching';
  return (
    <>
      <div className="cancel-flow-head">
        <h3>{t('cancel.whyTitle')}</h3>
        <button type="button" className="cancel-flow-icon-btn" onClick={onClose} aria-label={t('common.close') || 'Close'}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      </div>
      <p className="text-muted cancel-flow-hint">{t('cancel.whyHint')}</p>
      <div className="cancel-reason-list">
        {cancelReasonsFor(stage).map((key) => (
          <button key={key} type="button" className="cancel-reason-item cancel-reason-next" onClick={() => onPick(key)}>
            <span>{reasonLabel(t, key, stage)}</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
          </button>
        ))}
      </div>
      <button type="button" className="btn cancel-flow-keep" onClick={onClose}>
        {searching ? t('cancel.keepWaiting') : t('cancel.keep')}
      </button>
    </>
  );
}

const ACTION_COPY = {
  boost: { title: 'cancel.fu.boostTitle', sub: 'cancel.fu.boostSub' },
  call: { title: 'cancel.fu.callTitle', sub: 'cancel.fu.callSub' },
  chat: { title: 'cancel.fu.chatTitle', sub: 'cancel.fu.chatSub' },
  rebook: { title: 'cancel.fu.rebookTitle', sub: 'cancel.fu.rebookSub' },
  support: { title: 'cancel.fu.supportTitle', sub: 'cancel.fu.supportSub' },
};

/**
 * Step 2: the follow-up for the chosen reason. Offers a fix (boost, call,
 * chat, rebook, support) before the rider confirms; "Keep" stays the big
 * button. "Something else" adds an optional note.
 */
export function CancelFollowUpStep({
  t, stage, reason, policy, annaName, actions = {}, busy, note, onNote, onBack, onKeep, onConfirm,
}) {
  const searching = stage === 'searching';
  const name = annaName || 'Anna';
  const can = {
    boost: Boolean(actions.boost), call: Boolean(actions.call), chat: Boolean(actions.chat),
    rebook: Boolean(actions.rebook), support: Boolean(actions.support),
  };
  const fu = cancelFollowUp(reason, stage, can);
  const plainTitle = searching ? t('cancel.fu.plainTitleSearching') : t('cancel.fu.plainTitleRide');
  const plainBody = searching
    ? t('cancel.fu.plainBodySearching')
    : t(stage === 'arrived' ? 'cancel.fu.plainBodyArrived' : 'cancel.fu.plainBodyAssigned', { name });
  const copy = {
    waitSearching: [t('cancel.fu.waitSearchingTitle'), t('cancel.fu.waitSearchingBody')],
    waitAssigned: [t('cancel.fu.waitAssignedTitle', { name }), t('cancel.fu.waitAssignedBody')],
    waitArrived: [t('cancel.fu.waitArrivedTitle', { name }), t('cancel.fu.waitArrivedBody')],
    pickupSearching: [t('cancel.fu.pickupSearchingTitle'), t('cancel.fu.pickupSearchingBody')],
    pickupRide: [t('cancel.fu.pickupRideTitle'), t('cancel.fu.pickupRideBody', { name })],
    issue: [t('cancel.fu.issueTitle'), t('cancel.fu.issueBody')],
    foundOther: [plainTitle, t('cancel.fu.foundOtherBody')],
    plain: [plainTitle, plainBody],
  }[fu.kind];
  const action = fu.action;
  const actionVars = action === 'boost'
    ? { amount: actions.boost.amount, fare: actions.boost.fare }
    : { name };
  const runAction = action === 'boost' ? actions.boost.run : actions[action];
  const badge = { boost: `+₹${actions.boost?.amount ?? ''}`, call: 'call', chat: 'chat', rebook: 'pin', support: 'help' }[action];

  return (
    <>
      <div className="cancel-flow-head cancel-flow-head-back">
        <button type="button" className="cancel-flow-icon-btn" onClick={onBack} aria-label={t('cancel.backToReasons')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 12H5M11 6l-6 6 6 6" /></svg>
        </button>
        <h3>{copy[0]}</h3>
      </div>
      <span className="cancel-flow-chip">{t('cancel.reasonChip', { reason: reasonLabel(t, reason, stage) })}</span>
      <p className="cancel-flow-body">{copy[1]}</p>

      {action && (
        <button type="button" className="cancel-flow-fix" onClick={runAction} disabled={busy}>
          <span className={`cancel-flow-fix-badge is-${action}`} aria-hidden="true">
            {action === 'boost' ? badge : <FixIcon kind={action} />}
          </span>
          <span className="cancel-flow-fix-text">
            <strong>{t(ACTION_COPY[action].title, actionVars)}</strong>
            <span>{t(ACTION_COPY[action].sub, actionVars)}</span>
          </span>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
        </button>
      )}

      {fu.note && (
        <div className="cancel-flow-note">
          <label htmlFor="cancel-note">
            {t('cancel.noteLabel')} <span>{t('cancel.noteOptional')}</span>
          </label>
          <textarea
            id="cancel-note"
            rows={3}
            maxLength={CANCEL_NOTE_MAX}
            value={note}
            placeholder={t('cancel.notePlaceholder')}
            onChange={(e) => onNote(String(e.target.value || '').slice(0, CANCEL_NOTE_MAX))}
          />
          <span className="cancel-flow-note-foot">
            <span>{t('cancel.noteHint')}</span>
            <span>{`${note.length}/${CANCEL_NOTE_MAX}`}</span>
          </span>
        </div>
      )}

      <CancelPolicyBanner t={t} policy={policy} />

      <button type="button" className="btn cancel-flow-keep" onClick={onKeep} disabled={busy}>
        {searching ? t('cancel.keepWaiting') : t('cancel.keep')}
      </button>
      <button type="button" className="btn cancel-flow-confirm" onClick={onConfirm} disabled={busy}>
        {searching ? t('cancel.cancelRequest') : t('cancel.confirm')}
      </button>
    </>
  );
}

function FixIcon({ kind }) {
  const paths = {
    call: <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />,
    chat: <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />,
    pin: <><path d="M12 21s-6.5-6.2-6.5-11a6.5 6.5 0 0 1 13 0c0 4.8-6.5 11-6.5 11z" /><circle cx="12" cy="10" r="2.3" /></>,
    rebook: <><path d="M12 21s-6.5-6.2-6.5-11a6.5 6.5 0 0 1 13 0c0 4.8-6.5 11-6.5 11z" /><circle cx="12" cy="10" r="2.3" /></>,
    support: <><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6V14M12 17h.01" /></>,
  };
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {paths[kind]}
    </svg>
  );
}

/**
 * The rider's cancel flow: reason first (nothing pre-selected), then a
 * follow-up for that reason offering a fix, then the confirm. onConfirm gets
 * the exact string to save as ride.cancelReason (utils/cancelFlow.js).
 */
export default function CancelReasonModal({
  t,
  onConfirm,
  onClose,
  busy,
  stage = 'searching',
  acceptedAt = null,
  lateCount = 0,
  annaName = '',
  actions = {},
}) {
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState('');
  useScrollLock();

  const acceptedMs = acceptedAt?.toMillis ? acceptedAt.toMillis()
    : (acceptedAt?.seconds ? acceptedAt.seconds * 1000 : (Number(acceptedAt) || 0));

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (stage !== 'assigned' || !acceptedMs) return undefined;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [stage, acceptedMs]);

  const policy = cancelPolicy({ stage, acceptedMs, now, lateCount });

  return (
    <div className="modal-backdrop" role="presentation" onClick={busy ? undefined : onClose}>
      <div
        className="modal-sheet cancel-flow-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={t('cancel.whyTitle')}
        onClick={(e) => e.stopPropagation()}
      >
        {!reason ? (
          <CancelReasonsStep t={t} stage={stage} onPick={setReason} onClose={onClose} />
        ) : (
          <CancelFollowUpStep
            t={t}
            stage={stage}
            reason={reason}
            policy={policy}
            annaName={annaName}
            actions={actions}
            busy={busy}
            note={note}
            onNote={setNote}
            onBack={() => { setReason(null); setNote(''); }}
            onKeep={onClose}
            onConfirm={() => onConfirm(cancelReasonValue(reason, note))}
          />
        )}
      </div>
    </div>
  );
}
